import { useLocation } from "react-router-dom";

const Vesicle = ({ className, size = 300 }) => (
  <svg className={className} width={size} height={size} viewBox="0 0 200 200" fill="none" aria-hidden="true">
    <circle cx="100" cy="100" r="88" stroke="currentColor" strokeWidth="1" strokeDasharray="3 7" />
    <circle cx="100" cy="100" r="58" stroke="currentColor" strokeWidth="1" />
    <circle cx="100" cy="100" r="30" stroke="currentColor" strokeWidth="0.75" strokeDasharray="2 5" />
    <circle cx="100" cy="100" r="6" fill="currentColor" />
    <circle cx="158" cy="52" r="4" fill="currentColor" />
    <circle cx="42" cy="150" r="4" fill="currentColor" />
    <circle cx="100" cy="12" r="2.5" fill="currentColor" />
    <circle cx="188" cy="130" r="2.5" fill="currentColor" />
  </svg>
);

const Network = ({ className }) => (
  <svg className={className} width="420" height="380" viewBox="0 0 420 380" fill="none" aria-hidden="true">
    <g stroke="currentColor" strokeWidth="0.75">
      <path d="M60 80 L180 40 L300 90 L360 200 L250 300 L110 320 L50 190 Z" />
      <path d="M180 40 L250 300 M300 90 L110 320 M60 80 L360 200 M50 190 L300 90" />
    </g>
    {[[60, 80], [180, 40], [300, 90], [360, 200], [250, 300], [110, 320], [50, 190]].map(([x, y], i) => (
      <circle key={i} cx={x} cy={y} r={i % 3 === 0 ? 6 : 3.5} fill="currentColor" stroke="none" />
    ))}
    <circle cx="205" cy="180" r="16" stroke="currentColor" strokeWidth="1" fill="none" />
    <circle cx="205" cy="180" r="5" fill="currentColor" stroke="none" />
  </svg>
);

const Bars = ({ className }) => (
  <svg className={className} width="340" height="300" viewBox="0 0 340 300" fill="none" aria-hidden="true">
    <g stroke="currentColor" strokeWidth="1">
      {[0, 1, 2, 3, 4, 5].map((i) => (
        <line key={i} x1="0" y1={30 + i * 48} x2="340" y2={30 + i * 48} strokeDasharray={i === 2 ? "none" : "2 6"} />
      ))}
      <rect x="40" y="120" width="30" height="128" />
      <rect x="100" y="80" width="30" height="168" />
      <rect x="160" y="150" width="30" height="98" />
      <rect x="220" y="55" width="30" height="193" />
    </g>
  </svg>
);

const Crosshair = ({ className }) => (
  <svg className={className} width="20" height="20" viewBox="0 0 20 20" aria-hidden="true">
    <path d="M10 1v18M1 10h18" stroke="currentColor" strokeWidth="1" />
    <circle cx="10" cy="10" r="2.5" fill="currentColor" stroke="none" />
  </svg>
);

const Molecule = ({ className }) => (
  <svg className={className} width="260" height="240" viewBox="0 0 260 240" fill="none" aria-hidden="true">
    <g stroke="currentColor" strokeWidth="1">
      <path d="M130 40 L200 90 L200 160 L130 205 L60 160 L60 90 Z" />
      <path d="M130 40 L130 205 M60 90 L200 160 M200 90 L60 160" />
    </g>
    {[[130, 40], [200, 90], [200, 160], [130, 205], [60, 160], [60, 90]].map(([x, y], i) => (
      <circle key={i} cx={x} cy={y} r="5" fill="currentColor" stroke="none" />
    ))}
    <circle cx="130" cy="122" r="10" stroke="currentColor" strokeWidth="1" fill="none" />
  </svg>
);

const Label = ({ children, className }) => (
  <span className={`font-lab absolute hidden text-[9px] uppercase tracking-[0.3em] text-[#0e8c8c]/50 md:block ${className}`}>
    {children}
  </span>
);

const LAYERS = {
  home: () => (
    <>
      <Vesicle className="absolute -right-24 top-[8%] text-[#0e8c8c]/[0.13]" size={420} />
      <Vesicle className="absolute -left-32 top-[42%] text-[#0e8c8c]/[0.10]" size={360} />
      <Molecule className="absolute bottom-[4%] right-[6%] text-[#0a0a0a]/[0.05]" />
      <Crosshair className="absolute left-[8%] top-[30%] text-[#0e8c8c]/[0.25]" />
      <Crosshair className="absolute right-[38%] top-[64%] text-[#0e8c8c]/[0.2]" />
      <Label className="left-4 top-[38%]">EXOPLUS BIO — 30–150 NM</Label>
      <Label className="bottom-[6%] right-4 [writing-mode:vertical-rl]">SPECIMEN · FIG. 01</Label>
    </>
  ),
  product: () => (
    <>
      <Vesicle className="absolute -left-28 top-[6%] text-[#0e8c8c]/[0.12]" size={380} />
      <Molecule className="absolute bottom-[8%] right-[4%] text-[#0e8c8c]/[0.10]" />
      <Crosshair className="absolute right-[10%] top-[24%] text-[#0e8c8c]/[0.25]" />
      <Label className="right-4 top-[12%] [writing-mode:vertical-rl]">PRODUCT DOSSIER</Label>
    </>
  ),
  team: () => (
    <>
      <Network className="absolute -left-20 top-[10%] text-[#0e8c8c]/[0.12]" />
      <Network className="absolute -right-28 bottom-[4%] text-[#0e8c8c]/[0.09] -scale-x-100" />
      <Crosshair className="absolute right-[20%] top-[18%] text-[#0e8c8c]/[0.22]" />
      <Label className="left-4 bottom-[8%]">CHANNEL · FIVE ROLES · ONE STANDARD</Label>
    </>
  ),
  certification: () => (
    <>
      <Vesicle className="absolute -right-24 top-[5%] text-[#0e8c8c]/[0.12]" size={380} />
      <Molecule className="absolute bottom-[10%] left-[3%] text-[#0a0a0a]/[0.05]" />
      <Crosshair className="absolute left-[14%] top-[36%] text-[#0e8c8c]/[0.25]" />
      <Label className="right-4 top-[30%] [writing-mode:vertical-rl]">QC · LOT 08F49 · VERIFIED</Label>
    </>
  ),
  whitepaper: () => (
    <>
      <Bars className="absolute -right-10 top-[6%] text-[#0e8c8c]/[0.11]" />
      <Vesicle className="absolute -left-28 bottom-[6%] text-[#0e8c8c]/[0.10]" size={340} />
      <Crosshair className="absolute left-[10%] top-[20%] text-[#0e8c8c]/[0.22]" />
      <Label className="right-4 bottom-[10%] [writing-mode:vertical-rl]">STRATEGY · CHANNEL ECONOMICS</Label>
    </>
  ),
  admin: () => (
    <>
      <Vesicle className="absolute -left-24 bottom-[8%] text-[#0e8c8c]/[0.10]" size={300} />
      <Crosshair className="absolute right-[12%] top-[18%] text-[#0e8c8c]/[0.2]" />
      <Label className="right-4 bottom-[10%] [writing-mode:vertical-rl]">CONTROL ROOM</Label>
    </>
  ),
  default: () => <Vesicle className="absolute -right-20 top-[10%] text-[#0e8c8c]/[0.10]" size={300} />,
};

export const PageBackground = () => {
  const { pathname } = useLocation();
  const key = pathname === "/" ? "home"
    : pathname.startsWith("/products") ? "product"
    : pathname.startsWith("/team") ? "team"
    : pathname.startsWith("/certification") ? "certification"
    : pathname.startsWith("/whitepaper") ? "whitepaper"
    : pathname.startsWith("/admin") ? "admin"
    : "default";
  const Layer = LAYERS[key] || LAYERS.default;
  return (
    <div aria-hidden="true" className="pointer-events-none fixed inset-0 z-0 overflow-hidden" data-testid="page-background">
      <div
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(900px 600px at 88% -10%, rgba(14,140,140,0.07), transparent 60%), radial-gradient(700px 520px at -8% 28%, rgba(227,242,240,0.55), transparent 62%), radial-gradient(820px 620px at 50% 112%, rgba(244,243,239,0.85), transparent 60%)",
        }}
      />
      <div className="lab-grid absolute inset-0 opacity-30" />
      <Layer />
    </div>
  );
};

export default PageBackground;